// Landing-touch helpers for app.tachusa.com.
//
// The marketing site (tachusa.com) records the URL each visitor arrived on and
// the site that sent them in a `.tachusa.com` cookie (tach-landing-page
// `src/shared/landing.ts`). It is sent as `landing_touches` when a carrier's
// customer record is created in RoleSelect, so a signup with no ETID can be
// traced back to how they got here.
//
// Read-only here apart from clearing: only the marketing site writes it.

export const LANDING_COOKIE_NAME = 'tach_landing';

export interface LandingTouch {
  url: string;
  referrer: string | null;
  landed_at: string;
}

// `first` is the first arrival and never overwritten; `last` is the most
// recent arrival from outside tachusa.com.
export interface LandingTouches {
  first: LandingTouch;
  last: LandingTouch;
}

const isLandingTouch = (value: unknown): value is LandingTouch => {
  const touch = value as LandingTouch | null;
  return (
    typeof touch?.url === 'string' &&
    typeof touch.landed_at === 'string' &&
    (touch.referrer === null || typeof touch.referrer === 'string')
  );
};

// Re-validated on read so a hand-edited or truncated cookie never reaches the
// POST /customers body. Returns only the two known entries, nothing extra.
export const getLandingTouches = (): LandingTouches | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${LANDING_COOKIE_NAME}=`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match.split('=')[1] ?? ''));
    if (!isLandingTouch(parsed?.first) || !isLandingTouch(parsed?.last)) {
      return null;
    }
    const pick = ({ url, referrer, landed_at }: LandingTouch) => ({
      url,
      referrer,
      landed_at,
    });
    return { first: pick(parsed.first), last: pick(parsed.last) };
  } catch {
    return null;
  }
};

// Mirrors clearEtidCookie's scoping. Cleared once the customer record carries
// it, and on logout, so the next account created in this browser does not
// inherit someone else's arrival.
export const clearLandingCookie = () => {
  if (typeof document === 'undefined') return;
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isProduction = hostname.endsWith('tachusa.com');
  const domain = isProduction ? '; domain=.tachusa.com' : '';
  document.cookie = `${LANDING_COOKIE_NAME}=; path=/; max-age=0${domain}; samesite=lax`;
};
